import type { Milliseconds, Seconds } from '@lemonade-hq/ts-helpers';
import { NOOP, randomUuid } from '@lemonade-hq/ts-helpers';
import * as Toast from '@radix-ui/react-toast';
import { AnimatePresence, motion } from 'framer-motion';
import type { HTMLAttributes, PropsWithChildren } from 'react';
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { IconName } from '../Icon/Icon';
import * as styles from './Toast.css';
import { ToastItemComponent } from './ToastItem';
import { useToastContainerInteractions } from './useToastContainerInteractions';

export interface ToastAction {
  readonly label: string;
  readonly onAction: () => void;
}

export interface ToastProps {
  readonly icon?: IconName;
  readonly title: string;
  readonly action?: ToastAction;
  readonly style?: HTMLAttributes<HTMLDivElement>['style'];
  readonly type?: 'attention' | 'success' | 'system';
  readonly id?: string;
}

export type ToastOpenProps = ToastProps & { readonly timeout?: number };

interface ToastItem extends ToastProps {
  readonly id: string;
  readonly timeout: number;
}

interface ToastContextValue {
  readonly open: (props: ToastOpenProps | ToastOpenProps[]) => string;
  readonly close: (id?: string) => void;
  readonly closeAll: () => void;
  readonly setStyle: (style: HTMLAttributes<HTMLDivElement>['style']) => void;
}

const ToastContext = createContext<ToastContextValue>({
  open: () => '',
  close: NOOP,
  closeAll: NOOP,
  setStyle: NOOP,
});

const DEFAULT_TOAST_TIMEOUT: Milliseconds = 5000;
const STACK_COLLAPSE_TIMEOUT: Milliseconds = 8000;
export const ANIMATION_DURATION: Seconds = 0.4;

interface ToastProviderProps extends PropsWithChildren {
  readonly defaultTimeout?: Milliseconds;
  readonly stackCollapseTimeout?: Milliseconds;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  defaultTimeout = DEFAULT_TOAST_TIMEOUT,
  stackCollapseTimeout = STACK_COLLAPSE_TIMEOUT,
  children,
}) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [viewportStyle, setViewportStyle] = useState<HTMLAttributes<HTMLDivElement>['style']>({});
  const [expanded, setExpanded] = useState(true);
  const [interacting, setInteracting] = useState(false);
  const timerRefs = useRef<Map<string, number>>(new Map());
  const stackCollapseTimerRef = useRef<number | null>(null);

  const close = useCallback((id?: string) => {
    if (id != null) {
      setToasts(prev => prev.filter(toast => toast.id !== id));
      const timer = timerRefs.current.get(id);
      if (timer != null) {
        clearTimeout(timer);
        timerRefs.current.delete(id);
      }
    } else {
      setToasts(prev => (prev.length > 0 ? prev.slice(0, -1) : []));
    }
  }, []);

  const open = useCallback(
    (toastProps: ToastOpenProps | ToastOpenProps[]): string => {
      const toastPropsArray = Array.isArray(toastProps) ? toastProps : [toastProps];
      let lastToastId = '';

      setToasts(prev => {
        const prevIds = new Set(prev.map(t => t.id));
        const existingToasts: ToastItem[] = [];
        const newToasts: ToastItem[] = [];

        toastPropsArray.forEach(toast => {
          const { timeout = defaultTimeout, id, ...props } = toast;
          const toastId = id ?? `toast-id-${randomUuid()}`;
          lastToastId = toastId;

          const newToast: ToastItem = { ...props, id: toastId, timeout };

          if (!prevIds.has(toastId)) {
            newToasts.push(newToast);
          } else {
            existingToasts.push(newToast);
          }
        });

        const updatedIds = new Set([...newToasts.map(t => t.id), ...existingToasts.map(t => t.id)]);
        const unchangedToasts = prev.filter(t => !updatedIds.has(t.id));
        const result = [...unchangedToasts, ...newToasts.reverse(), ...existingToasts];

        return result;
      });

      return lastToastId;
    },
    [defaultTimeout],
  );

  const closeAll = useCallback(() => {
    setToasts([]);
    setExpanded(true);
    timerRefs.current.forEach(timer => clearTimeout(timer));
    timerRefs.current.clear();
    /* istanbul ignore next -- @preserve - Timer null check is defensive and timing-dependent in tests */
    if (stackCollapseTimerRef.current != null) {
      clearTimeout(stackCollapseTimerRef.current);
      stackCollapseTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (toasts.length > 1) {
      setExpanded(true);

      timerRefs.current.forEach(timer => clearTimeout(timer));
      timerRefs.current.clear();

      if (stackCollapseTimerRef.current != null) {
        clearTimeout(stackCollapseTimerRef.current);
      }

      stackCollapseTimerRef.current = window.setTimeout(() => {
        /* istanbul ignore else -- @preserve - Edge case: user interacting exactly when timer fires is untestable */
        if (!interacting) {
          setExpanded(false);

          toasts.forEach(toast => {
            if (toast.timeout !== Infinity) {
              const timer = window.setTimeout(() => {
                close(toast.id);
              }, toast.timeout);
              timerRefs.current.set(toast.id, timer);
            }
          });
        }
      }, stackCollapseTimeout);
    } else {
      setExpanded(true);
      if (stackCollapseTimerRef.current != null) {
        clearTimeout(stackCollapseTimerRef.current);
        stackCollapseTimerRef.current = null;
      }

      if (toasts.length === 1) {
        const toast = toasts[0];
        /* istanbul ignore else -- @preserve - Timer existence check is for race condition prevention */
        if (toast.timeout !== Infinity && !timerRefs.current.has(toast.id)) {
          const timer = window.setTimeout(() => {
            close(toast.id);
          }, toast.timeout);
          timerRefs.current.set(toast.id, timer);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We don't want to re-run this effect when closing toasts
  }, [toasts.length, stackCollapseTimeout, interacting]);

  useEffect(() => {
    const timers = timerRefs.current;
    /* istanbul ignore next -- @preserve - Cleanup on unmount is not testable with current setup */
    return () => {
      timers.forEach(timer => clearTimeout(timer));
      timers.clear();
      if (stackCollapseTimerRef.current != null) clearTimeout(stackCollapseTimerRef.current);
    };
  }, []);

  const setStyle = useCallback((style: HTMLAttributes<HTMLDivElement>['style']) => {
    setViewportStyle(style);
  }, []);

  const contextValue = useMemo(() => {
    return { toasts, open, close, closeAll, setStyle };
  }, [toasts, open, close, closeAll, setStyle]);

  const containerHandlers = useToastContainerInteractions({
    expanded,
    setExpanded,
    setInteracting,
    toastCount: toasts.length,
  });

  return (
    <ToastContext.Provider value={contextValue}>
      <Toast.Provider swipeDirection="up">
        {children}
        <AnimatePresence mode="popLayout">
          {toasts.length > 0 && (
            <motion.div
              {...containerHandlers}
              className={styles.toastContainer}
              data-testid="toast-container"
              key="toast-container"
            >
              {toasts.map((toast, index) => (
                <ToastItemComponent
                  expanded={expanded}
                  index={index}
                  key={toast.id}
                  onClose={close}
                  toast={toast}
                  totalToasts={toasts.length}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        <Toast.Viewport className={styles.toastViewport} style={viewportStyle} />
      </Toast.Provider>
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  return useContext(ToastContext);
}
